import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import OtpInput from 'react-otp-input'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { OutlinedButton } from '../../../components/Button'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'
import { AppDispatch } from '../../../store'
import {
  StorageUtils,
  VerificationActions,
  VerificationSelector,
} from '../../../store/Verification'
import { useEffect } from 'react'

export const VerifyOtp = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { showSuccess, showError } = useSnackbar()
  const {
    verifyLoading,
    resendLoading,
    otp,
    inputEmail: email,
  } = useSelector(VerificationSelector.getVerificationState)

  const handleOtpChange = (value: string) => {
    dispatch(VerificationActions.setOtp(value))
  }

  const handleVerifyOtp = async () => {
    try {
      const resultAction: any = await dispatch(VerificationActions.verifyOtpAsync({ email, otp }))
      if (VerificationActions.verifyOtpAsync.fulfilled.match(resultAction)) {

        StorageUtils.setVerificationState({
          email: resultAction.payload.user.email,
          isVerified: true,
          token: resultAction.payload.token,
          timestamp: Date.now(),
        })
        const AUTH_PATHS = ['/test-strip/email', '/verify/otp']
        const from = location.state?.from
        const url = from && !AUTH_PATHS.includes(from?.pathname) ? from : '/test-strip/data'

        navigate(url)
        dispatch(VerificationActions.resetFormFields())
      }
      if(VerificationActions.verifyOtpAsync.rejected.match(resultAction)) {
        showError((resultAction.payload as any).message)
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  const handleResendOtp = async () => {
    try {
      const resultAction: any = await dispatch(VerificationActions.sendOtpAsync(email))
      if (VerificationActions.sendOtpAsync.fulfilled.match(resultAction)) {
        showSuccess(resultAction.payload.message || 'Verification code resent')
      } else {
        showError(resultAction?.payload?.error || 'Failed to resend code')
      }
    } catch (err) {
      showError('Something went wrong. Please try again.')
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        minHeight: 'calc(100vh - 68px)',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Stack alignItems="center" spacing={3.5} sx={{ maxWidth: '450px' }}>
            <Stack alignItems="center">
              <Typography
                variant="h2"
                textAlign={'center'}
              >
                Check your email
              </Typography>

              <Typography variant="body1" textAlign={'center'}>
                We've sent a verification code to{' '}
                <Typography component="span" style={{ fontWeight: 700 }}>{email}</Typography>
              </Typography>
            </Stack>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              numInputs={6}
              renderInput={(props) => (
                <input
                  {...props}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, '')
                    if (props.onChange) props.onChange(e)
                  }}
                />
              )}
              inputStyle={{
                margin: '4px',
                width: isMobile ? '45px' : '55.6px',
                height: isMobile ? '45px' : '56px',
                borderRadius: '8px',
                fontSize: '24px',
                backgroundColor: 'white',
                border: `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
                outline: 'none',
              }}
              inputType="text"
              shouldAutoFocus
            />

            <Stack spacing={1} width="100%">
              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{ py: 2 }}
                disableElevation
                endIcon={<ArrowForward />}
                disabled={verifyLoading || !otp || otp.length !== 6}
                onClick={handleVerifyOtp}
              >
                {verifyLoading ? 'Verifying...' : 'Verify code'}
              </Button>
              <OutlinedButton
                fullWidth
                disabled={resendLoading}
                onClick={handleResendOtp}
                sx={{
                  border: 'none',
                  py: 1.5,
                }}
              >
                {resendLoading ? 'Resending...' : 'Resend code'}
              </OutlinedButton>

              <OutlinedButton
                fullWidth
                sx={{
                  border: 'none',
                  color: theme.palette.grey[600],
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: theme.palette.grey[500],
                  },
                }}
                onClick={() => {
                  dispatch(VerificationActions.clearVerification())
                  navigate('/verify/email')
                }}
                startIcon={<ArrowBack fontSize="small" />}
              >
                change email address
              </OutlinedButton>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}
